import React from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  toWSDateString,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSImage,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryPlanEnrollment } from "../../../query/benefits/useQueryPlanEnrollment";
import { DataViewSection } from "../../../shared/components/DataView";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import {
  PARAMETER_MY_RESOURCES_PLAN_ENROLLMENT_ID,
  routeHelpersMyResourcesTabs
} from "../paths";
import MeridioHealthInsuranceImage from "../../Benefits/assets/MeridioHorizontalLogo.png";
import { MeridioNeedToMakeChanges } from "../../Benefits/components/customBenefits/Meridio/MeridioNeedToMakeChanges";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";

type Props = RouteComponentProps<{
  [PARAMETER_MY_RESOURCES_PLAN_ENROLLMENT_ID]: string;
}>;

export const RoutePersonalInsurancePolicyDetails: React.FC<Props> = ({
  match
}) => {
  const goToMyResourcesTab = routeHelpersMyResourcesTabs.useGoTo();
  const planEnrollmentId =
    match.params[PARAMETER_MY_RESOURCES_PLAN_ENROLLMENT_ID];
  const qPlanEnrollment = useQueryPlanEnrollment(planEnrollmentId);

  return (
    <WSPage colorBackground="gray50">
      <WSQueries queries={{ qPlanEnrollment }}>
        {({ qPlanEnrollmentData }) => {
          const name = qPlanEnrollmentData.name;

          return (
            <>
              <WSPageToolbar
                title={name}
                actions={[
                  {
                    label: "Contact support",
                    icon: "support",
                    helpText: "To cancel this policy, please contact support."
                  }
                ]}
                breadcrumbs={[
                  {
                    label: "My resources",
                    onClick: () =>
                      goToMyResourcesTab({ tab: "personal-insurance" })
                  },
                  {
                    label: name
                  }
                ]}
              />

              <WSGrid>
                <WSGrid.Item span={{ s: "12", m: "8" }}>
                  <WSList gap="XL">
                    <DataViewSection
                      gridColumns={3}
                      title="Policy information"
                      data={[
                        {
                          label: "Plan name",
                          value: name
                        },
                        {
                          label: "SKU",
                          value: qPlanEnrollmentData.sku
                        },
                        {
                          label: "Monthly premium",
                          value: formatMoney(
                            qPlanEnrollmentData.monthlyPremium,
                            Currency.USD
                          )
                        },
                        {
                          label: "Status",
                          pill: selectorInsuranceCoverageStatusPillByExpirationDate(
                            qPlanEnrollmentData.endDate
                          ),
                          value: null,
                          hideValue: true,
                          secondaryText: [
                            `Effective on ${toWSDateString(
                              qPlanEnrollmentData.effectiveDate,
                              "monthDayYear"
                            )}`,
                            qPlanEnrollmentData.endDate
                              ? `Ending on ${toWSDateString(
                                  qPlanEnrollmentData.endDate,
                                  "monthDayYear"
                                )}`
                              : null
                          ]
                            .filter(Boolean)
                            .join("\n")
                        }
                      ]}
                    >
                      <WSInfoBox title="Filing a claim" icon="file">
                        For detailed claims issues, contact the carrier first
                        before contacting Meridio customer care. Contact Meridio
                        customer care at{" "}
                        <a href="tel:929-581-1801">929-581-1801</a>.
                      </WSInfoBox>
                      <MeridioNeedToMakeChanges />
                    </DataViewSection>
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ s: "12", m: "4" }}>
                  <WSElement>
                    <WSSectionToolbar title="Source" />
                    <WSCard mt="S" divider>
                      <WSList gap="L">
                        <WSFlexBox justify="center">
                          <WSImage
                            src={MeridioHealthInsuranceImage}
                            placeholder={<></>}
                            width={160}
                          />
                        </WSFlexBox>
                        <WSInfoBox>
                          This policy was activated from an insurance benefit
                          you enrolled in on{" "}
                          {toWSDateString(
                            qPlanEnrollmentData.effectiveDate,
                            "monthDayYear"
                          )}
                        </WSInfoBox>
                      </WSList>
                    </WSCard>
                  </WSElement>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
